import { useEffect, useId, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import CreatorCourses from '../components/courses/CreatorCourses'
import toast from 'react-hot-toast'
import { ShieldCheckIcon, CurrencyDollarIcon, XMarkIcon, CreditCardIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { creatorProfileAPI, monetizationAPI, publicCreatorsAPI, tipGoalsAPI } from '../services/api'
import type { TipGoal } from '../types/tipGoals'
import TipGoalProgress from '../components/monetization/TipGoalProgress'
import CreatorPolls from '../components/polls/CreatorPolls'
import InviteFriends from '../components/referrals/InviteFriends'
import { creatorRecordPath } from '../routes'
import type { PublicProfileResponse, Subscription, SubscriptionTier, PaymentMethod } from '../types'
import LoadingScreen from '../components/LoadingScreen'
import { apiErrorMessage } from '../utils/apiError'
import { completeCardPayment } from '../services/cardPayment'
import Tooltip from '../components/Tooltip'
import { TRACK_RECORD_GLOSSARY } from '../config/glossary'
import PricingPlans from '../components/monetization/PricingPlans'
import SubscribeModal from '../components/monetization/SubscribeModal'
import { mediaUrl } from '../utils/mediaUrl'

// ---- Tip Modal ----
interface TipModalProps {
  isOpen: boolean
  onClose: () => void
  creatorId: number
  creatorName: string
  /** A contribution to this goal (charged like a tip) instead of a plain tip. */
  goal?: TipGoal | null
  onSent?: () => void
}

const PRESET_AMOUNTS = [1, 5, 10, 25]
// The server's minimum (TipCreate.amount_cents >= 100).
const MIN_TIP_CENTS = 100

const TipModal = ({ isOpen, onClose, creatorId, creatorName, goal, onSent }: TipModalProps) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null)
  const [selectedAmount, setSelectedAmount] = useState<number | null>(5)
  const [customAmount, setCustomAmount] = useState('')
  const [message, setMessage] = useState('')
  const [isLoadingMethods, setIsLoadingMethods] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const titleId = useId()

  useEffect(() => {
    if (!isOpen) return
    setIsLoadingMethods(true)
    monetizationAPI.getPaymentMethods()
      .then((methods: PaymentMethod[]) => {
        setPaymentMethods(methods)
        const def = methods.find((m) => m.is_default) || methods[0]
        setSelectedMethodId(def ? def.id : null)
      })
      .catch(() => toast.error('Failed to load payment methods'))
      .finally(() => setIsLoadingMethods(false))
  }, [isOpen])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose()
    }
    if (isOpen) document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, isSubmitting, onClose])

  const amountCents = () => {
    if (selectedAmount !== null) return selectedAmount * 100
    const v = parseFloat(customAmount)
    return isNaN(v) || v <= 0 ? 0 : Math.round(v * 100)
  }

  const handleSubmit = async () => {
    if (!selectedMethodId || amountCents() < MIN_TIP_CENTS) return
    setIsSubmitting(true)
    try {
      const charge = goal
        ? await tipGoalsAPI.contribute(goal.id, {
            amount_cents: amountCents(),
            payment_method_id: selectedMethodId,
            message: message.trim() || undefined,
          })
        : await monetizationAPI.sendTip({
            creator_id: creatorId,
            amount_cents: amountCents(),
            payment_method_id: selectedMethodId,
            message: message.trim() || undefined,
            is_anonymous: false,
          })
      // 3-D Secure: the bank's approval step, when the card asks for one.
      const outcome = await completeCardPayment(charge)
      if (outcome.status === 'failed') {
        toast.error(outcome.message)
        return
      }
      const approved = charge.payment_status === 'requires_action'
      toast.success(
        goal
          ? approved
            ? 'Thanks for contributing! It will show on the goal in a moment.'
            : 'Thanks for contributing!'
          : 'Tip sent!'
      )
      onSent?.()
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, goal ? 'Failed to contribute' : 'Failed to send tip'))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900">
            {goal ? `Contribute to “${goal.title}”` : `Tip ${creatorName}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
            <div className="flex gap-2 flex-wrap mb-3">
              {PRESET_AMOUNTS.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => { setSelectedAmount(amt); setCustomAmount('') }}
                  className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                    selectedAmount === amt
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  ${amt}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setSelectedAmount(null)}
                className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                  selectedAmount === null
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Custom
              </button>
            </div>
            {selectedAmount === null && (
              <input
                type="number"
                min="1"
                step="0.01"
                placeholder="Enter amount in USD (minimum $1)"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              rows={3}
              placeholder="Leave a message for the creator..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
            {isLoadingMethods ? (
              <p className="text-sm text-gray-500">Loading payment methods...</p>
            ) : paymentMethods.length === 0 ? (
              <p className="text-sm text-gray-500">No payment methods found. Please add one in Monetization settings.</p>
            ) : (
              <div className="space-y-2">
                {paymentMethods.map((method) => (
                  <label
                    key={method.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedMethodId === method.id
                        ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="tip-payment-method"
                      value={method.id}
                      checked={selectedMethodId === method.id}
                      onChange={() => setSelectedMethodId(method.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <CreditCardIcon className="h-5 w-5 text-gray-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900">
                      {method.brand || 'Card'} ****{method.last4}
                    </span>
                    {method.is_default && (
                      <span className="inline-flex items-center text-xs text-success-600 ml-auto">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Default
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedMethodId || amountCents() < MIN_TIP_CENTS}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : `Send $${selectedAmount ?? (parseFloat(customAmount) || 0).toFixed(2)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---- Main page ----
const CreatorProfile = () => {
  const { id } = useParams<{ id: string }>()
  const creatorId = Number(id)

  const [profile, setProfile] = useState<PublicProfileResponse | null>(null)
  // The creator's handle when they have a public call record; null otherwise.
  const [recordHandle, setRecordHandle] = useState<string | null>(null)
  const [tiers, setTiers] = useState<SubscriptionTier[]>([])
  const [currentTierId, setCurrentTierId] = useState<number | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [isTiersLoading, setIsTiersLoading] = useState(true)
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false)
  const [isTipModalOpen, setIsTipModalOpen] = useState(false)
  const [tipGoals, setTipGoals] = useState<TipGoal[]>([])
  const [contributingTo, setContributingTo] = useState<TipGoal | null>(null)
  // A live subscription to this creator: the invite panel shows.
  const [subscribedNow, setSubscribedNow] = useState(false)

  useEffect(() => {
    if (Number.isNaN(creatorId)) return

    loadProfile()
    loadTiers()
    loadCurrentSubscription()
    loadTipGoals()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId])

  // Open goals only; a profile with none shows nothing.
  const loadTipGoals = () => {
    tipGoalsAPI
      .list(creatorId)
      .then(setTipGoals)
      .catch(() => setTipGoals([]))
  }

  const loadProfile = async () => {
    setIsLoading(true)
    try {
      const data = await creatorProfileAPI.getPublicProfile(creatorId)
      setProfile(data)
      // Looked up by the username the profile has now: a 404 (no public
      // call) leaves the link off.
      publicCreatorsAPI
        .get(data.creator.username)
        .then((record) => setRecordHandle(record.creator.handle))
        .catch(() => setRecordHandle(null))
    } catch (error) {
      console.error('Error loading creator profile:', error)
      toast.error('Failed to load creator profile')
    } finally {
      setIsLoading(false)
    }
  }

  const loadTiers = async () => {
    setIsTiersLoading(true)
    try {
      const data = await monetizationAPI.getTiers(creatorId)
      setTiers(data)
    } catch (error) {
      console.error('Error loading subscription tiers:', error)
      toast.error('Failed to load subscription plans')
    } finally {
      setIsTiersLoading(false)
    }
  }

  const loadCurrentSubscription = async () => {
    try {
      const subscriptions = await monetizationAPI.getSubscriptions()
      const existing = subscriptions.find((s) => s.creator_id === creatorId)
      setCurrentTierId(existing?.tier_id)
      setSubscribedNow(
        subscriptions.some((s) => s.creator_id === creatorId && (s.status === 'active' || s.status === 'trialing'))
      )
    } catch (error) {
      console.error('Error loading subscriptions:', error)
    }
  }

  const handleSelectTier = (tier: SubscriptionTier, cycle: 'monthly' | 'yearly') => {
    setSelectedTier(tier)
    setBillingCycle(cycle)
    setIsSubscribeModalOpen(true)
  }

  const handleSubscribed = (subscription: Subscription) => {
    setCurrentTierId(subscription.tier_id)
    setSubscribedNow(subscription.status === 'active' || subscription.status === 'trialing')
    setIsSubscribeModalOpen(false)
  }

  if (isLoading) return <LoadingScreen />

  if (!profile) {
    return <div className="max-w-3xl mx-auto py-12 text-center text-gray-600">Creator not found.</div>
  }

  const { creator, profile: creatorProfile, stats } = profile
  const displayName = creator.full_name || creator.username
  const initials = displayName.slice(0, 2).toUpperCase()

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-primary-500 to-primary-700" />

        <div className="px-6 pb-6">
          <div className="flex items-end justify-between -mt-12 mb-4">
            <div className="relative">
              {creator.avatar_url ? (
                <img
                  src={mediaUrl(creator.avatar_url)}
                  alt={displayName}
                  className="h-24 w-24 rounded-full border-4 border-white object-cover"
                />
              ) : (
                <div className="h-24 w-24 rounded-full border-4 border-white bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-600 font-bold text-2xl">{initials}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              {creatorProfile.is_verified && (
                <div className="flex items-center gap-1 text-primary-600 text-sm font-medium">
                  <ShieldCheckIcon className="h-5 w-5" />
                  Verified
                </div>
              )}
              <button
                onClick={() => setIsTipModalOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors"
              >
                <CurrencyDollarIcon className="h-4 w-4" />
                Tip
              </button>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
          <p className="text-gray-500">@{creator.username}</p>

          {creatorProfile.tagline && (
            <p className="mt-2 text-gray-700 font-medium">{creatorProfile.tagline}</p>
          )}
          {creatorProfile.bio && (
            <p className="mt-3 text-gray-700 text-sm leading-relaxed">{creatorProfile.bio}</p>
          )}

          {/* A creator's results live on their call record (/c/<handle>),
              computed by the Track Record's own functions. The stored "Signal
              Win Rate" and "Signals" snapshot that used to sit here counted a
              different set of signals and could disagree with it. */}
          {recordHandle && (
            <p className="mt-4">
              <Link
                to={creatorRecordPath(recordHandle)}
                className="text-sm font-medium text-primary-600 hover:underline"
                data-testid="profile-record-link"
              >
                See every call by @{recordHandle} →
              </Link>
            </p>
          )}

          <div className="mt-5 grid grid-cols-2 gap-3">
            {stats.avg_rr_ratio != null && (
              <div className="bg-primary-50 rounded-lg p-3 text-center">
                <p className="text-xs text-primary-700 font-medium uppercase tracking-wide">
                  <Tooltip content={TRACK_RECORD_GLOSSARY.avgSignalRR}>Avg Signal R:R</Tooltip>
                </p>
                <p className="text-xl font-bold text-primary-700 mt-0.5">{`${stats.avg_rr_ratio.toFixed(2)}x`}</p>
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-600 font-medium uppercase tracking-wide">Subscribers</p>
              <p className="text-xl font-bold text-gray-800 mt-0.5">{stats.active_subscribers}</p>
            </div>
          </div>
        </div>
      </div>

      <CreatorCourses creatorId={creatorId} />

      {tipGoals.length > 0 && (
        <section aria-labelledby="tip-goals-heading" className="space-y-3">
          <h2 id="tip-goals-heading" className="text-lg font-semibold text-gray-900">
            Tip goals
          </h2>
          {tipGoals.map((g) => (
            <TipGoalProgress
              key={g.id}
              goal={g}
              action={
                <button
                  type="button"
                  onClick={() => setContributingTo(g)}
                  className="shrink-0 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
                >
                  Contribute
                </button>
              }
            />
          ))}
        </section>
      )}

      <CreatorPolls creatorId={creatorId} />

      {subscribedNow && <InviteFriends creatorId={creatorId} creatorName={displayName} />}

      <PricingPlans
        tiers={tiers}
        onSelectTier={handleSelectTier}
        currentTierId={currentTierId}
        isLoading={isTiersLoading}
      />

      <SubscribeModal
        isOpen={isSubscribeModalOpen}
        onClose={() => setIsSubscribeModalOpen(false)}
        tier={selectedTier}
        billingCycle={billingCycle}
        onSubscribed={handleSubscribed}
      />

      <TipModal
        isOpen={isTipModalOpen}
        onClose={() => setIsTipModalOpen(false)}
        creatorId={creatorId}
        creatorName={displayName}
      />

      <TipModal
        isOpen={contributingTo !== null}
        onClose={() => setContributingTo(null)}
        creatorId={creatorId}
        creatorName={displayName}
        goal={contributingTo}
        onSent={loadTipGoals}
      />
    </div>
  )
}

export default CreatorProfile
